/**
 * Organisation (Mandant) — Seite für Kundenadmins und Reviewer.
 *
 * Zeigt die Organisation(en) des Benutzers und pflegt den eigenen
 * KI-Schlüssel (D15): Ist er gesetzt, laufen alle KI-Aufrufe dieser
 * Organisation darüber; sonst gilt der Betreiber-Schlüssel von netz-fabrik.
 * Der Wert wird nach dem Speichern nie wieder angezeigt.
 *
 * Benutzer werden hier NICHT verwaltet — das geschieht im netzfabriks-erp
 * (Zielbild: das ERP ist führend). Diese Seite sagt das ausdrücklich.
 */
import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { KeyRound, Users } from "lucide-react"
import { toast } from "sonner"
import { api, apiError } from "@/services/api"
import { useAuthStore } from "@/store/auth"
import type { Organisation } from "@/types"

function KiKeyKarte({ org }: { org: Organisation }) {
  const queryClient = useQueryClient()
  const [wert, setWert] = useState("")
  const [bearbeiten, setBearbeiten] = useState(false)

  const fertig = () => {
    setWert("")
    setBearbeiten(false)
    void queryClient.invalidateQueries({ queryKey: ["organisationen"] })
  }
  const setzen = useMutation({
    mutationFn: () => api.organisationen.kiKeySetzen(org.slug, wert.trim()),
    onSuccess: () => {
      toast.success("KI-Schlüssel gespeichert")
      fertig()
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const entfernen = useMutation({
    mutationFn: () => api.organisationen.kiKeyEntfernen(org.slug),
    onSuccess: () => {
      toast.success("KI-Schlüssel entfernt — es gilt wieder der Schlüssel von netz-fabrik")
      fertig()
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <div className="rounded-xl border bg-card p-5 shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold">{org.name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Kennung <code className="rounded bg-muted px-1">{org.slug}</code>
          </p>
        </div>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-bold ${
            org.kiKeyGesetzt ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
          }`}
        >
          {org.kiKeyGesetzt ? "Eigener KI-Schlüssel" : "Schlüssel von netz-fabrik"}
        </span>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-lg bg-muted/40 p-4 text-sm">
        <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">KI-Schlüssel dieser Organisation</p>
          <p className="mt-1 text-muted-foreground">
            Hinterlegt ihr einen eigenen Anthropic-API-Schlüssel, laufen alle KI-Aufrufe eurer
            Meldungen darüber und werden über euer Konto abgerechnet. Ohne eigenen Schlüssel
            gilt der Schlüssel von netz-fabrik. Der Wert wird verschlüsselt gespeichert und
            nie wieder angezeigt.
          </p>
          {org.kiKeyGesetzt && org.kiKeyGesetztAm && (
            <p className="mt-2 text-xs text-muted-foreground">
              Gesetzt am {new Date(org.kiKeyGesetztAm).toLocaleString("de-DE")}
            </p>
          )}

          {bearbeiten ? (
            <form
              className="mt-3 flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                if (wert.trim().length >= 20) setzen.mutate()
              }}
            >
              <input
                type="password"
                autoComplete="off"
                value={wert}
                onChange={(e) => setWert(e.target.value)}
                placeholder="sk-ant-…"
                className="min-w-[18rem] flex-1 rounded-md border bg-background px-3 py-2 text-sm"
              />
              <button
                type="submit"
                disabled={wert.trim().length < 20 || setzen.isPending}
                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
              >
                Speichern
              </button>
              <button
                type="button"
                onClick={fertig}
                className="rounded-md px-3 py-2 text-sm font-semibold hover:bg-secondary"
              >
                Abbrechen
              </button>
            </form>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setBearbeiten(true)}
                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary-hover"
              >
                {org.kiKeyGesetzt ? "Schlüssel ersetzen" : "Eigenen Schlüssel hinterlegen"}
              </button>
              {org.kiKeyGesetzt && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("Eigenen KI-Schlüssel entfernen?")) entfernen.mutate()
                  }}
                  disabled={entfernen.isPending}
                  className="rounded-md px-3 py-2 text-sm font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-50"
                >
                  Entfernen
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function OrganisationPage() {
  const darfPflegen = useAuthStore((s) => s.isKundenadmin() || s.isReviewerVoll())
  const { data, isLoading, error } = useQuery({
    queryKey: ["organisationen"],
    queryFn: api.organisationen.list,
  })

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-bold">Organisation</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Eure Organisation im Testerwerk — und der KI-Schlüssel, über den eure Meldungen
          laufen.
        </p>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
        <Users className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div>
          <p className="font-semibold">Benutzer werden im netzfabriks-erp verwaltet</p>
          <p className="mt-1 text-muted-foreground">
            Wer sich hier anmelden darf und welche Rolle (Melder, Kundenadmin) jemand hat,
            legt netz-fabrik im ERP am Ansprechpartner fest. Neue Zugänge, Rollenwechsel
            oder Sperrungen bitte dort anstoßen — das Testerwerk übernimmt sie beim nächsten
            Login.
          </p>
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Lade …</p>}
      {error && <p className="text-sm text-destructive">{apiError(error)}</p>}
      {data?.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Du bist keiner Organisation zugeordnet. Bitte bei netz-fabrik melden.
        </p>
      )}
      {darfPflegen
        ? data?.map((org) => <KiKeyKarte key={org.slug} org={org} />)
        : data?.map((org) => (
            <div key={org.slug} className="rounded-xl border bg-card p-5 shadow-card">
              <h2 className="text-lg font-bold">{org.name}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Den KI-Schlüssel pflegt euer Kundenadmin.
              </p>
            </div>
          ))}
    </div>
  )
}
